import { ControlBar, ErrorBanner } from '@/components';
import { OTElementId } from '@/constants';
import { AccountPlan } from '@/constants/enum';
import { isLockFeature } from '@/helpers/feature';
import { getErrorFromAPI, handleToastMutation } from '@/helpers/toast';
import {
  useCreateRawCourierMaskMutation,
  useDeleteRawCourierMaskMutation,
  useGetListCourierQuery,
  useGetRawCourierMasksQuery,
  useGetRawCourierOptionsQuery,
  useUpdateRawCourierMaskMutation,
} from '@/redux/api/api.caller';
import commonSlice from '@/redux/features/common.slice';
import { accountSelector } from '@/redux/features/plan.slice';
import { IRawCourierMask } from '@/types/settings';
import {
  BlockStack,
  Button,
  ButtonGroup,
  InlineStack,
  Modal,
  Select,
  SkeletonBodyText,
  Text,
  TextField,
} from '@shopify/polaris';
import { DeleteIcon, EditIcon } from '@shopify/polaris-icons';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

interface IModalState {
  open: boolean;
  id: number | null;
  from: string;
  mode: 'list' | 'custom';
  to: string;
}

const FALLBACK_DISPLAY_NAMES = ['USPS', 'UPS', 'FedEx', 'DHL Express', 'Royal Mail', 'Australia Post', 'Canada Post', 'Colissimo'];

const BLANK_MODAL: IModalState = { open: false, id: null, from: '', mode: 'list', to: '' };

const MaskingRules = () => {
  const plan = useSelector(accountSelector);
  const isLock = isLockFeature([AccountPlan.Starter, AccountPlan.Basic]) && !plan.isOldPaidUser;
  const dispatch = useDispatch();
  const [apiError, setApiError] = useState<{ update: string }>({ update: '' });
  const [createMask, createMaskStatus] = useCreateRawCourierMaskMutation();
  const [updateMask, updateMaskStatus] = useUpdateRawCourierMaskMutation();
  const [deleteMask, deleteMaskStatus] = useDeleteRawCourierMaskMutation();
  const { data: listCourierData } = useGetListCourierQuery({ page: 1, perPage: 500 }, { skip: isLock });
  const { data: optionsData, isLoading } = useGetRawCourierOptionsQuery({ page: 1, perPage: 500 }, { skip: isLock });
  const masksQuery = useGetRawCourierMasksQuery(undefined, { skip: isLock });
  const fetchedMasks = masksQuery.data?.data;

  const [listCourierMapping, setListCourierMapping] = useState<Array<IRawCourierMask>>([]);
  const [loadedMapping, setLoadedMapping] = useState<Array<IRawCourierMask>>([]);
  const [modal, setModal] = useState<IModalState>(BLANK_MODAL);

  const displayNameValues = useMemo(() => {
    const fromApi = (listCourierData?.data?.couriers ?? []).map(({ display_name }) => display_name).filter(Boolean);
    return fromApi.length ? fromApi : FALLBACK_DISPLAY_NAMES;
  }, [listCourierData]);
  const displayNameOptions = useMemo(() => displayNameValues.map((v) => ({ label: v, value: v })), [displayNameValues]);
  const actualCarrierOptions = useMemo(() => {
    const used = new Set(listCourierMapping.map((r) => r.rawCourier));
    return (optionsData?.data?.rawCouriers ?? [])
      .filter(({ rawCourier }) => rawCourier === modal.from || !used.has(rawCourier))
      .map(({ rawCourier }) => ({ label: rawCourier, value: rawCourier }));
  }, [optionsData, listCourierMapping, modal.from]);

  const tempIdRef = useRef(-1);
  const isSavingRef = useRef(false);

  const { added, deletedIds, updated, isDirty } = useMemo(() => {
    const added = listCourierMapping.filter((r) => r.id < 0);
    const listIds = listCourierMapping.map((r) => r.id);
    const deletedIds = loadedMapping.filter((b) => !listIds.includes(b.id)).map((b) => b.id);
    const updated = listCourierMapping.filter((r) => {
      if (r.id < 0) return false;
      const base = loadedMapping.find((b) => b.id === r.id);
      return !!base && base.maskCourier !== r.maskCourier;
    });
    const isDirty = added.length > 0 || deletedIds.length > 0 || updated.length > 0;
    return { added, deletedIds, updated, isDirty };
  }, [listCourierMapping, loadedMapping]);

  const isDirtyRef = useRef(isDirty);
  isDirtyRef.current = isDirty;

  useEffect(() => {
    if (!fetchedMasks) return;
    if (isSavingRef.current || isDirtyRef.current) return;
    const data = [...fetchedMasks].reverse();
    setListCourierMapping(data);
    setLoadedMapping(data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchedMasks]);

  const closeModal = () => setModal(BLANK_MODAL);
  const patchModal = (patch: Partial<IModalState>) => setModal((prev) => ({ ...prev, ...patch }));

  const handleOpenAdd = () => {
    setApiError({ update: '' });
    setModal({ ...BLANK_MODAL, open: true });
  };

  const handleOpenEdit = (item: IRawCourierMask) => () => {
    setApiError({ update: '' });
    setModal({
      open: true,
      id: item.id,
      from: item.rawCourier,
      mode: displayNameValues.includes(item.maskCourier) ? 'list' : 'custom',
      to: item.maskCourier,
    });
  };

  const isEdit = modal.id !== null;
  const canSubmit = !!modal.from && (modal.mode === 'list' ? !!modal.to : !!modal.to.trim());

  const handleSubmitModal = () => {
    if (!canSubmit) return;
    const to = modal.mode === 'custom' ? modal.to.trim() : modal.to;
    if (isEdit) {
      setListCourierMapping((prev) => prev.map((r) => (r.id === modal.id ? { ...r, maskCourier: to } : r)));
    } else {
      setListCourierMapping((prev) => [{ id: tempIdRef.current, rawCourier: modal.from, maskCourier: to }, ...prev]);
      tempIdRef.current -= 1;
    }
    closeModal();
  };

  const handleRemoveRow = (id: number) => () => {
    setApiError({ update: '' });
    setListCourierMapping((prev) => prev.filter((r) => r.id !== id));
  };

  const handleDiscard = () => {
    setListCourierMapping(loadedMapping);
    setApiError({ update: '' });
  };

  const isOk = (res: any) => 'data' in res && res.data && res.data?.state === 1;

  const handleSave = async () => {
    isSavingRef.current = true;
    try {
      const delResults = await Promise.all(deletedIds.map((id) => deleteMask(id)));
      const createResults = await Promise.all(
        added.map(({ rawCourier, maskCourier }) => createMask({ rawCourier, maskCourier })),
      );
      const updateResults = await Promise.all(updated.map(({ id, maskCourier }) => updateMask({ id, maskCourier })));

      const allResults = [...delResults, ...createResults, ...updateResults];
      const allOk = allResults.length > 0 && allResults.every(isOk);

      const fresh = await masksQuery.refetch();
      const serverList = [...(fresh.data?.data ?? [])].reverse();
      setLoadedMapping(serverList);

      if (allOk) {
        setListCourierMapping(serverList);
        setApiError({ update: '' });
        const okRes = allResults.find(isOk);
        if (okRes) dispatch(commonSlice.actions.handleToast(handleToastMutation(okRes)));
      } else {
        const next = [...serverList];
        updated.forEach((r, i) => {
          if (!isOk(updateResults[i])) {
            const idx = next.findIndex((x) => x.id === r.id);
            if (idx !== -1) next[idx] = { ...next[idx], maskCourier: r.maskCourier };
          }
        });
        const failedCreates = added.filter((_, i) => !isOk(createResults[i]));
        setListCourierMapping([...failedCreates, ...next]);
        const firstFailed = allResults.find((res) => !isOk(res));
        setApiError({ update: getErrorFromAPI(firstFailed) });
      }
    } finally {
      isSavingRef.current = false;
    }
  };

  const displaySelectValue = displayNameValues.includes(modal.to) ? modal.to : '';

  return (
    <>
      <ControlBar
        isFixed
        saveBarId={OTElementId.SaveBarCourierMapping}
        hidden={!isDirty}
        actionGroup={[
          { content: 'Discard', onAction: handleDiscard, primary: false },
          {
            content: 'Save',
            onAction: handleSave,
            primary: true,
            loading: createMaskStatus.isLoading || updateMaskStatus.isLoading || deleteMaskStatus.isLoading,
          },
        ]}
      />
      <BlockStack gap="300">
        <InlineStack align="space-between" blockAlign="center">
          <Text as="h2" variant="headingMd">
            Carrier masking
          </Text>
          <Button variant="primary" onClick={handleOpenAdd} disabled={isLock}>
            Add carrier
          </Button>
        </InlineStack>
        <Text as="p" variant="bodySm" tone="subdued">
          Rename a carrier your customers see on the tracking page. This changes the carrier label only. To also hide a
          carrier name that appears inside tracking events, use Find and replace words below.
        </Text>
        <ErrorBanner isVisible={!!apiError.update}>{apiError.update}</ErrorBanner>

        {isLoading ? (
          <SkeletonBodyText />
        ) : listCourierMapping.length === 0 ? (
          <div className="cm-empty">
            <Text as="p" variant="bodySm" tone="subdued">
              You haven&apos;t masked any carriers yet
            </Text>
          </div>
        ) : (
          <div>
            {listCourierMapping.map((item) => (
              <div key={item.id} className="cm-item">
                <InlineStack gap="200" blockAlign="center">
                  <Text as="span" variant="bodyMd">
                    {item.rawCourier}
                  </Text>
                  <Text as="span" variant="bodyMd" tone="subdued">
                    →
                  </Text>
                  <Text as="span" variant="bodyMd">
                    {item.maskCourier}
                  </Text>
                </InlineStack>
                <ButtonGroup>
                  <Button
                    variant="plain"
                    icon={DeleteIcon}
                    onClick={handleRemoveRow(item.id)}
                    disabled={isLock}
                    accessibilityLabel="Delete"
                  />
                  <Button
                    variant="plain"
                    icon={EditIcon}
                    onClick={handleOpenEdit(item)}
                    disabled={isLock}
                    accessibilityLabel="Edit"
                  />
                </ButtonGroup>
              </div>
            ))}
          </div>
        )}
      </BlockStack>

      <Modal
        open={modal.open}
        onClose={closeModal}
        title={isEdit ? 'Edit carrier masking' : 'Add carrier masking'}
        primaryAction={{ content: isEdit ? 'Save' : 'Add', onAction: handleSubmitModal, disabled: !canSubmit }}
        secondaryActions={[{ content: 'Cancel', onAction: closeModal }]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            <Select
              label="Actual carrier"
              placeholder="Choose carrier"
              options={actualCarrierOptions}
              value={modal.from}
              onChange={(v) => patchModal({ from: v })}
              disabled={isEdit}
            />
            <BlockStack gap="150">
              <Text as="span" variant="bodyMd">
                Show as
              </Text>
              <ButtonGroup variant="segmented">
                <Button pressed={modal.mode === 'list'} onClick={() => patchModal({ mode: 'list', to: displaySelectValue })}>
                  Another carrier
                </Button>
                <Button
                  pressed={modal.mode === 'custom'}
                  onClick={() => patchModal({ mode: 'custom', to: modal.mode === 'custom' ? modal.to : '' })}
                >
                  Custom name
                </Button>
              </ButtonGroup>
              {modal.mode === 'list' ? (
                <Select
                  label="Show as"
                  labelHidden
                  placeholder="Choose a carrier"
                  options={displayNameOptions}
                  value={displaySelectValue}
                  onChange={(v) => patchModal({ to: v })}
                />
              ) : (
                <TextField
                  label="Show as"
                  labelHidden
                  autoComplete="off"
                  placeholder="e.g. Standard Shipping"
                  maxLength={50}
                  showCharacterCount
                  value={modal.to}
                  onChange={(v) => patchModal({ to: v })}
                />
              )}
              <Text as="span" variant="bodySm" tone="subdued">
                Updates the carrier label only. To hide a carrier name inside tracking events, use Find and replace.
              </Text>
            </BlockStack>
          </BlockStack>
        </Modal.Section>
      </Modal>
    </>
  );
};

export default MaskingRules;
